/**
 * <NewHireOnboardingForm /> — manager creates a new hire and provisions
 * their onboarding workflow.
 *
 * Route: /admin/onboarding/new
 *
 * Flow:
 *   1. Manager fills name, email, phone, hire date, template.
 *   2. We create the employee record with a temp password (existing
 *      addEmployee + create-employee-account function).
 *   3. We call createOnboardingForEmployee which provisions checklist
 *      items, progress doc, and sends the welcome email + SMS.
 *   4. Redirect to the manager's view of the new employee.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useApp } from '../../../context/AppContext';
import { useToast } from '../../../components/Toast';
import { createOnboardingForEmployee } from '../services/onboardingService';
import '../Onboarding.css';

function generateTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let p = '';
  for (let i = 0; i < 10; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
  return p;
}

export default function NewHireOnboardingForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const { addEmployee } = useApp();

  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    hireDate: new Date().toISOString().slice(0, 10),
    templateId: 'residential_cleaner_v1',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const q = query(collection(db, 'onboarding_templates'), where('active', '==', true));
      const snap = await getDocs(q);
      setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) errs.email = 'Valid email required';
    if (!form.phone.trim()) errs.phone = 'Required';
    if (!form.hireDate) errs.hireDate = 'Required';
    if (!form.templateId) errs.templateId = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const tempPassword = generateTempPassword();
      const newEmp = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        hireDate: form.hireDate,
        hire_date: form.hireDate,
        role: 'employee',
        status: 'onboarding',
        legal_name: `${form.firstName.trim()} ${form.lastName.trim()}`,
        _createLogin: true,
        _tempPassword: tempPassword,
      };
      const created = await addEmployee(newEmp);
      const employeeId = created?.id || (Array.isArray(created) ? created[0]?.id : null);
      if (!employeeId) throw new Error('Could not determine new employee id');

      await createOnboardingForEmployee({
        employeeId,
        templateId: form.templateId,
      });

      toast.success(`Welcome message sent to ${form.email} and ${form.phone}`);
      navigate(`/admin/onboarding/${employeeId}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create new hire');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-shell">
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif' }}>New hire</h1>
      <p style={{ color: '#555' }}>Create the employee record and kick off their onboarding.</p>

      <form onSubmit={onSubmit}>
        <div className="onboarding-form-row">
          <label>First name</label>
          <input name="firstName" type="text" value={form.firstName} onChange={onChange} />
          {errors.firstName && <div className="field-error">{errors.firstName}</div>}
        </div>
        <div className="onboarding-form-row">
          <label>Last name</label>
          <input name="lastName" type="text" value={form.lastName} onChange={onChange} />
          {errors.lastName && <div className="field-error">{errors.lastName}</div>}
        </div>
        <div className="onboarding-form-row">
          <label>Email</label>
          <input name="email" type="email" value={form.email} onChange={onChange} />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>
        <div className="onboarding-form-row">
          <label>Phone</label>
          <input name="phone" type="tel" value={form.phone} onChange={onChange} />
          {errors.phone && <div className="field-error">{errors.phone}</div>}
        </div>
        <div className="onboarding-form-row">
          <label>Hire date</label>
          <input name="hireDate" type="date" value={form.hireDate} onChange={onChange} />
          {errors.hireDate && <div className="field-error">{errors.hireDate}</div>}
        </div>
        <div className="onboarding-form-row">
          <label>Onboarding template</label>
          <select name="templateId" value={form.templateId} onChange={onChange}>
            {templates.length === 0 && <option value="residential_cleaner_v1">Residential Cleaner v1</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.version})</option>
            ))}
          </select>
          {errors.templateId && <div className="field-error">{errors.templateId}</div>}
        </div>

        <button type="submit" className="onboarding-btn" disabled={submitting} style={{ width: '100%', marginTop: 12 }}>
          {submitting ? 'Creating…' : 'Create new hire & send welcome'}
        </button>
      </form>
    </div>
  );
}
